// biome-ignore-all lint/a11y/useAriaPropsSupportedByRole: shared component anatomy.
// biome-ignore-all lint/a11y/useFocusableInteractive: shared component anatomy.
// biome-ignore-all lint/a11y/noAutofocus: shared component anatomy.
// biome-ignore-all lint/style/useTemplate: shared component anatomy.
// biome-ignore-all lint/complexity/useIndexOf: shared component anatomy.
// biome-ignore-all lint/security/noDangerouslySetInnerHtml: shared component anatomy.
// biome-ignore-all lint/suspicious/noArrayIndexKey: shared component anatomy.
// biome-ignore-all lint/a11y/useSemanticElements: shared component anatomy.
// biome-ignore-all lint/a11y/noInteractiveElementToNoninteractiveRole: shared component anatomy.
// biome-ignore-all lint/a11y/noStaticElementInteractions: shared component anatomy.
// biome-ignore-all lint/a11y/useKeyWithClickEvents: shared component anatomy.
// biome-ignore-all lint/a11y/noSvgWithoutTitle: shared component anatomy.
// biome-ignore-all lint/correctness/useExhaustiveDependencies: shared component anatomy.
"use client";

/**
 * jalco-ui
 * CodeBlockCommand
 * by Justin Levine
 * ui.justinlevine.me
 *
 * Tabbed package manager command block with copy button, persistent tab selection,
 * and bundled SVG icons with optional overrides.
 *
 * Props:
 * - pnpm/yarn/npm/bun/shadcn?: command strings
 * - icons?: custom ReactNode icons keyed by manager (merged over defaults)
 * - iconStyle?: "none" | "colored" | "muted"
 * - show?: ordered subset of managers to display
 * - colorTheme?: { bg, fg } for editor-style code area coloring
 *
 * Dependencies: lucide-react
 */

import { cn } from "@thilke/ui/lib/utils";
import { Check, Copy } from "lucide-react";
import * as React from "react";

type PackageManager = "pnpm" | "yarn" | "npm" | "bun" | "shadcn";

const STORAGE_KEY = "jalco-ui-pkg-manager";

const managers: PackageManager[] = ["pnpm", "yarn", "npm", "bun", "shadcn"];

const defaultIcons: Record<PackageManager, string> = {
  pnpm: `<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" preserveAspectRatio="xMidYMid meet" viewBox="76.58987244897958 44 164.00775510204068 164" width="160.01" height="160"><defs><path d="M237.6 95L187.6 95L187.6 45L237.6 45L237.6 95Z" id="arNRoK435"></path><path d="M182.59 95L132.59 95L132.59 45L182.59 45L182.59 95Z" id="a3H2WU7Px"></path><path d="M127.59 95L77.59 95L77.59 45L127.59 45L127.59 95Z" id="b1DInM56vl"></path><path d="M237.6 150L187.6 150L187.6 100L237.6 100L237.6 150Z" id="a7LFlgQIwu"></path><path d="M182.59 150L132.59 150L132.59 100L182.59 100L182.59 150Z" id="amwLiZcuo"></path><path d="M182.59 205L132.59 205L132.59 155L182.59 155L182.59 205Z" id="f3Peu5RWan"></path><path d="M237.6 205L187.6 205L187.6 155L237.6 155L237.6 205Z" id="a6DXBfqPa"></path><path d="M127.59 205L77.59 205L77.59 155L127.59 155L127.59 205Z" id="c1GWSTH1z7"></path></defs><g><g><use xlink:href="#arNRoK435" opacity="1" fill="#f9ad00" fill-opacity="1"></use></g><g><use xlink:href="#a3H2WU7Px" opacity="1" fill="#f9ad00" fill-opacity="1"></use></g><g><use xlink:href="#b1DInM56vl" opacity="1" fill="#f9ad00" fill-opacity="1"></use></g><g><use xlink:href="#a7LFlgQIwu" opacity="1" fill="#f9ad00" fill-opacity="1"></use></g><g><use xlink:href="#amwLiZcuo" opacity="1" fill="#4e4e4e" fill-opacity="1"></use></g><g><use xlink:href="#f3Peu5RWan" opacity="1" fill="#4e4e4e" fill-opacity="1"></use></g><g><use xlink:href="#a6DXBfqPa" opacity="1" fill="#4e4e4e" fill-opacity="1"></use></g><g><use xlink:href="#c1GWSTH1z7" opacity="1" fill="#4e4e4e" fill-opacity="1"></use></g></g></svg>`,
  npm: `<svg viewBox="0 0 2500 2500" xmlns="http://www.w3.org/2000/svg" width="2500" height="2500"><path fill="#c00" d="M0 0h2500v2500H0z"/><path fill="#fff" d="M1241.5 268.5h-973v1962.9h972.9V763.5h495v1467.9h495V268.5z"/></svg>`,
  yarn: `<svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 518 518"><style>.st0{fill:#2c8ebb}.st1{fill:#fff}</style><path class="st0" d="M259 0c143 0 259 116 259 259S402 518 259 518 0 402 0 259 116 0 259 0z"/><path class="st1" d="M435.2 337.5c-1.8-14.2-13.8-24-29.2-23.8-23 .3-42.3 12.2-55.1 20.1-5 3.1-9.3 5.4-13 7.1.8-11.6.1-26.8-5.9-43.5-7.3-20-17.1-32.3-24.1-39.4 8.1-11.8 19.2-29 24.4-55.6 4.5-22.7 3.1-58-7.2-77.8-2.1-4-5.6-6.9-10-8.1-1.8-.5-5.2-1.5-11.9.4C293.1 96 289.6 93.8 286.9 92c-5.6-3.6-12.2-4.4-18.4-2.1-8.3 3-15.4 11-22.1 25.2-1 2.1-1.9 4.1-2.7 6.1-12.7.9-32.7 5.5-49.6 23.8-2.1 2.3-6.2 4-10.5 5.6h.1c-8.8 3.1-12.8 10.3-17.7 23.3-6.8 18.2.2 36.1 7.1 47.7-9.4 8.4-21.9 21.8-28.5 37.5-8.2 19.4-9.1 38.4-8.8 48.7-7 7.4-17.8 21.3-19 36.9-1.6 21.8 6.3 36.6 9.8 42 1 1.6 2.1 2.9 3.3 4.2-.4 2.7-.5 5.6.1 8.6 1.3 7 5.7 12.7 12.4 16.3 13.2 7 31.6 10 45.8 2.9 5.1 5.4 14.4 10.6 31.3 10.6h1c4.3 0 58.9-2.9 74.8-6.8 7.1-1.7 12-4.7 15.2-7.4 10.2-3.2 38.4-12.8 65-30 18.8-12.2 25.3-14.8 39.3-18.2 13.6-3.3 22.1-15.7 20.4-29.4zm-23.8 14.7c-16 3.8-24.1 7.3-43.9 20.2-30.9 20-64.7 29.3-64.7 29.3s-2.8 4.2-10.9 6.1c-14 3.4-66.7 6.3-71.5 6.4-12.9.1-20.8-3.3-23-8.6-6.7-16 9.6-23 9.6-23s-3.6-2.2-5.7-4.2c-1.9-1.9-3.9-5.7-4.5-4.3-2.5 6.1-3.8 21-10.5 27.7-9.2 9.3-26.6 6.2-36.9.8-11.3-6 .8-20.1.8-20.1s-6.1 3.6-11-3.8c-4.4-6.8-8.5-18.4-7.4-32.7 1.2-16.3 19.4-32.1 19.4-32.1s-3.2-24.1 7.3-48.8c9.5-22.5 35.1-40.6 35.1-40.6s-21.5-23.8-13.5-45.2c5.2-14 7.3-13.9 9-14.5 6-2.3 11.8-4.8 16.1-9.5 21.5-23.2 48.9-18.8 48.9-18.8s13-39.5 25-31.8c3.7 2.4 17 32 17 32s14.2-8.3 15.8-5.2c8.6 16.7 9.6 48.6 5.8 68-6.4 32-22.4 49.2-28.8 60-1.5 2.5 17.2 10.4 29 43.1 10.9 29.9 1.2 55 2.9 57.8.3.5.4.7.4.7s12.5 1 37.6-14.5c13.4-8.3 29.3-17.6 47.4-17.8 17.5-.3 18.4 20.2 5.2 23.4z"/></svg>`,
  bun: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 70"><path d="M71.09 20.74c-.16-.17-.33-.34-.5-.5s-.33-.34-.5-.5-.33-.34-.5-.5-.33-.34-.5-.5-.33-.34-.5-.5-.33-.34-.5-.5-.33-.34-.5-.5A26.46 26.46 0 0 1 75.5 35.7c0 16.57-16.82 30.05-37.5 30.05-11.58 0-21.94-4.23-28.83-10.86l.5.5.5.5.5.5.5.5.5.5.5.5.5.5C19.55 65.3 30.14 69.75 42 69.75c20.68 0 37.5-13.48 37.5-30 0-7.06-3.04-13.75-8.41-19.01Z"/><path d="M73 35.7c0 15.21-15.67 27.54-35 27.54S3 50.91 3 35.7C3 26.27 9 17.94 18.22 13S33.18 3 38 3s8.94 4.13 19.78 10C67 17.94 73 26.27 73 35.7Z" style="fill:#fbf0df"/><path data-name="Bottom Shadow" d="M73 35.7a21.67 21.67 0 0 0-.8-5.78c-2.73 33.3-43.35 34.9-59.32 24.94A40 40 0 0 0 38 63.24c19.3 0 35-12.35 35-27.54Z" style="fill:#f6dece"/><path data-name="Light Shine" d="M24.53 11.17C29 8.49 34.94 3.46 40.78 3.45A9.29 9.29 0 0 0 38 3c-2.42 0-5 1.25-8.25 3.13-1.13.66-2.3 1.39-3.54 2.15-2.33 1.44-5 3.07-8 4.7C8.69 18.13 3 26.62 3 35.7v1.19c6.06-21.41 17.07-23.04 21.53-25.72Z" style="fill:#fffefc"/><path d="M35.12 5.53A16.41 16.41 0 0 1 29.49 18c-.28.25-.06.73.3.59 3.37-1.31 7.92-5.23 6-13.14-.08-.45-.67-.33-.67.08Zm2.27 0A16.24 16.24 0 0 1 39 19c-.12.35.31.65.55.36 2.19-2.8 4.1-8.36-1.62-14.36-.29-.26-.74.14-.54.49Zm2.76-.17A16.42 16.42 0 0 1 47 17.12a.33.33 0 0 0 .65.11c.92-3.49.4-9.44-7.17-12.53-.4-.16-.66.38-.33.62Zm-18.46 10.4a16.94 16.94 0 0 0 10.47-9c.18-.36.75-.22.66.18-1.73 8-7.52 9.67-11.12 9.45-.38.01-.37-.52-.01-.63Z" style="fill:#ccbea7;fill-rule:evenodd"/><path d="M38 65.75C17.32 65.75.5 52.27.5 35.7c0-10 6.18-19.33 16.53-24.92 3-1.6 5.57-3.21 7.86-4.62 1.26-.78 2.45-1.51 3.6-2.19C32 1.89 35 .5 38 .5s5.62 1.2 8.9 3.14c1 .57 2 1.19 3.07 1.87 2.49 1.54 5.3 3.28 9 5.27C69.32 16.37 75.5 25.69 75.5 35.7c0 16.57-16.82 30.05-37.5 30.05ZM38 3c-2.42 0-5 1.25-8.25 3.13-1.13.66-2.3 1.39-3.54 2.15-2.33 1.44-5 3.07-8 4.7C8.69 18.13 3 26.62 3 35.7c0 15.19 15.7 27.55 35 27.55S73 50.89 73 35.7c0-9.08-5.69-17.57-15.22-22.7-3.78-2-6.73-3.88-9.12-5.36-1.09-.67-2.09-1.29-3-1.84C42.63 4 40.42 3 38 3Z"/><g><path d="M45.05 43a8.93 8.93 0 0 1-2.92 4.71 6.81 6.81 0 0 1-4 1.88A6.84 6.84 0 0 1 34 47.71 8.93 8.93 0 0 1 31.12 43a.72.72 0 0 1 .8-.81h12.34a.72.72 0 0 1 .79.81Z" style="fill:#b71422" data-name="Background"/><path data-name="Background" d="M34 47.79a6.91 6.91 0 0 0 4.12 1.9 6.91 6.91 0 0 0 4.11-1.9 10.63 10.63 0 0 0 1-1.07 6.83 6.83 0 0 0-4.9-2.31 6.15 6.15 0 0 0-5 2.78c.23.21.43.41.67.6Z" style="fill:#ff6164"/><path data-name="Outline" d="M34.16 47a5.36 5.36 0 0 1 4.19-2.08 6 6 0 0 1 4 1.69c.23-.25.45-.51.66-.77a7 7 0 0 0-4.71-1.93 6.36 6.36 0 0 0-4.89 2.36 9.53 9.53 0 0 0 .75.73Z"/><path data-name="Outline" d="M38.09 50.19a7.42 7.42 0 0 1-4.45-2 9.52 9.52 0 0 1-3.11-5.05 1.2 1.2 0 0 1 .26-1 1.41 1.41 0 0 1 1.13-.51h12.34a1.44 1.44 0 0 1 1.13.51 1.19 1.19 0 0 1 .25 1 9.52 9.52 0 0 1-3.11 5.05 7.42 7.42 0 0 1-4.44 2Zm-6.17-7.4c-.16 0-.2.07-.21.09a8.29 8.29 0 0 0 2.73 4.37A6.23 6.23 0 0 0 38.09 49a6.28 6.28 0 0 0 3.65-1.73 8.3 8.3 0 0 0 2.72-4.37.21.21 0 0 0-.2-.09Z"/></g><g><ellipse data-name="Right Blush" cx="53.22" cy="40.18" rx="5.85" ry="3.44" style="fill:#febbd0"/><ellipse data-name="Left Bluch" cx="22.95" cy="40.18" rx="5.85" ry="3.44" style="fill:#febbd0"/><path d="M25.7 38.8a5.51 5.51 0 1 0-5.5-5.51 5.51 5.51 0 0 0 5.5 5.51Zm24.77 0A5.51 5.51 0 1 0 45 33.29a5.5 5.5 0 0 0 5.47 5.51Z" style="fill-rule:evenodd"/><path d="M24 33.64a2.07 2.07 0 1 0-2.06-2.07A2.07 2.07 0 0 0 24 33.64Zm24.77 0a2.07 2.07 0 1 0-2.06-2.07 2.07 2.07 0 0 0 2.04 2.07Z" style="fill:#fff;fill-rule:evenodd"/></g></svg>`,
  shadcn: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><path fill="none" d="M0 0h256v256H0z"/><path fill="none" stroke="currentColor" stroke-width="25" stroke-linecap="round" d="M208 128l-80 80M192 40L40 192"/></svg>`,
};

type IconStyle = "none" | "colored" | "muted";

interface CodeBlockCommandProps
  extends Omit<React.ComponentProps<"div">, "children"> {
  pnpm?: string;
  yarn?: string;
  npm?: string;
  bun?: string;
  shadcn?: string;
  /**
   * Custom icons keyed by package manager name. Merged over built-in icons.
   * Pass a ReactNode per manager to override any default icon.
   */
  icons?: Partial<Record<PackageManager, React.ReactNode>>;
  /**
   * Icon display style:
   * - `"none"` — no icons shown
   * - `"colored"` — full-color icons (default)
   * - `"muted"` — grayscale + reduced opacity icons
   */
  iconStyle?: IconStyle;
  /**
   * Which tabs to display, in order. Only managers listed here (that also
   * have a command) will render. When omitted, all managers with a command
   * are shown in the default order.
   *
   * @example ["yarn", "bun", "shadcn"]
   */
  show?: PackageManager[];
  /**
   * Editor color theme for the code area.
   * Provide `{ bg, fg }` hex strings to override the default styling.
   */
  colorTheme?: { bg: string; fg: string };
  className?: string;
}

function DefaultIcon({ svg, muted }: { svg: string; muted: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex size-3.5 shrink-0 [&>svg]:size-full",
        muted && "grayscale opacity-50",
      )}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export function CodeBlockCommand({
  pnpm,
  yarn,
  npm,
  bun,
  shadcn,
  icons = {},
  iconStyle = "colored",
  show,
  colorTheme,
  className,
  ...props
}: CodeBlockCommandProps) {
  const commands = React.useMemo<Partial<Record<PackageManager, string>>>(
    () => ({
      ...(pnpm && { pnpm }),
      ...(yarn && { yarn }),
      ...(npm && { npm }),
      ...(bun && { bun }),
      ...(shadcn && { shadcn }),
    }),
    [pnpm, yarn, npm, bun, shadcn],
  );

  const available = (show ?? managers).filter((m) => commands[m]);

  const [active, setActive] = React.useState<PackageManager>(
    available[0] ?? "pnpm",
  );

  // Sync with localStorage after hydration to avoid mismatch
  React.useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as PackageManager | null;
    if (stored && commands[stored]) setActive(stored);
  }, [commands]);

  const [copied, setCopied] = React.useState(false);

  function handleSelect(manager: PackageManager) {
    setActive(manager);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, manager);
    }
  }

  async function handleCopy() {
    const command = commands[active];
    if (!command) return;
    await navigator.clipboard.writeText(command);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  const currentCommand = commands[active] ?? "";
  const isMuted = iconStyle === "muted";

  function renderIcon(manager: PackageManager) {
    if (iconStyle === "none") return null;

    const custom = icons[manager];
    if (custom !== undefined) return custom;

    const svg = defaultIcons[manager];
    if (!svg) return null;
    return <DefaultIcon svg={svg} muted={isMuted} />;
  }

  return (
    <div
      data-slot="code-block-command"
      className={cn(
        "overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm",
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between border-b border-border/60 bg-muted/40">
        <div className="flex" role="tablist" aria-label="Package manager">
          {available.map((manager) => (
            <button
              key={manager}
              type="button"
              role="tab"
              aria-selected={active === manager}
              onClick={() => handleSelect(manager)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors",
                active === manager
                  ? "border-b-2 border-foreground text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {renderIcon(manager)}
              {manager}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-2.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          aria-label={copied ? "Copied command" : "Copy command"}
        >
          {copied ? (
            <Check className="size-3.5" />
          ) : (
            <Copy className="size-3.5" />
          )}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div
        data-slot="code-block-command"
        className="overflow-x-auto px-4 py-3"
        style={
          colorTheme
            ? { backgroundColor: colorTheme.bg, color: colorTheme.fg }
            : undefined
        }
      >
        <pre className="m-0">
          <code
            className={cn(
              "font-mono text-[13px]",
              !colorTheme && "text-foreground",
            )}
          >
            <span
              className={cn(!colorTheme && "text-muted-foreground")}
              style={
                colorTheme
                  ? { color: `${colorTheme.fg}80`, userSelect: "none" }
                  : { userSelect: "none" }
              }
            >
              ${" "}
            </span>
            {currentCommand}
          </code>
        </pre>
      </div>
    </div>
  );
}
